import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle2, Copy, Eye, Loader2, RefreshCw, Send, Smartphone,
} from 'lucide-react';
import { formatToLocalDateTime } from '@/lib/dateUtils';

/**
 * Entry badges → the Kaizen Events member app (backend: modules/member_app_bridge).
 *
 * NOT the "Kaizen Events" tab next to this one. That one links to events.yatrimitra.com and pulls paid
 * registrations IN to become badges here. This one pushes finished badges OUT to a different product
 * that happens to share the name: the member app platform at dashboard.kaizenevents.live, where members
 * open their badge on their phone. Separate backend module, separate collections, separate token,
 * opposite direction. They must not be merged.
 *
 * Badge generation is untouched — this only forwards what Kaizen-QR has already made.
 */

const fmt = (v) => (v ? formatToLocalDateTime(v) : 'Never');
const selectClass = 'w-full h-9 rounded-md border border-input bg-background px-2 text-sm';

// The member app answers with one of these per badge. 'unchanged' means it already had that exact
// image for that ticket — the expected answer for most of a re-run, not a problem.
const GOOD = ['matched', 'unchanged'];

const MemberAppBridge = ({ eventId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [link, setLink] = useState(null);
  const [form, setForm] = useState({ base_url: '', target_event_id: '', token: '' });
  const [advanced, setAdvanced] = useState({ chapter_field: '', image_format: 'jpeg', image_width: 1200, image_quality: 86 });
  const [attendees, setAttendees] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState('');
  const [preview, setPreview] = useState(null);
  const [task, setTask] = useState(null);
  const [history, setHistory] = useState([]);
  const poll = useRef(null);
  const previewUrl = useRef(null);

  const applyLink = useCallback((d) => {
    setLink(d);
    // The token is never sent back to us (only has_token), so the field stays blank: typing in it
    // replaces the stored token, leaving it alone keeps it.
    setForm({ base_url: d.base_url || '', target_event_id: d.target_event_id || '', token: '' });
    setAdvanced({
      chapter_field: d.chapter_field || '',
      image_format: d.image_format || 'jpeg',
      image_width: d.image_width || 1200,
      image_quality: d.image_quality || 86,
    });
  }, []);

  // The picker comes from the bridge's own endpoint, not /events/{id}/attendees: that one is
  // paginated at 50 and would silently offer only the 50 newest attendees while showing a count
  // that looks like the event total.
  const loadHolders = useCallback(async (term) => {
    try {
      const res = await api.get(`/member-app-bridge/events/${eventId}/badge-holders`,
        { params: term ? { search: term } : undefined });
      setAttendees(res.data?.attendees || []);
      setTruncated(!!res.data?.truncated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load the attendee list');
    }
  }, [eventId]);

  const load = useCallback(async () => {
    try {
      const [cfg, hist] = await Promise.all([
        api.get(`/member-app-bridge/events/${eventId}/config`),
        api.get(`/member-app-bridge/events/${eventId}/sync`),
      ]);
      applyLink(cfg.data);
      setHistory(hist.data || []);
      await loadHolders('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load the member app bridge');
    } finally {
      setLoading(false);
    }
  }, [eventId, applyLink, loadHolders]);

  useEffect(() => { load(); }, [load]);

  // Stop polling and release the blob URL when this tab goes away, so a sync left running does not
  // keep hitting the API from a screen nobody is looking at.
  useEffect(() => () => {
    if (poll.current) clearInterval(poll.current);
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
  }, []);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const body = {
        base_url: form.base_url,
        target_event_id: form.target_event_id,
        chapter_field: advanced.chapter_field,
        image_format: advanced.image_format,
        image_width: Number(advanced.image_width) || 1200,
        image_quality: Number(advanced.image_quality) || 86,
        ...(form.token ? { token: form.token } : {}),
        ...extra,
      };
      const res = await api.put(`/member-app-bridge/events/${eventId}/config`, body);
      applyLink(res.data);
      toast.success(
        extra.enabled === true ? 'Bridge is on — badges can now be pushed'
          : extra.enabled === false ? 'Bridge paused'
            : form.token ? 'Saved — new key stored' : 'Saved');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!picked) return;
    try {
      const res = await api.get(
        `/member-app-bridge/events/${eventId}/attendees/${picked}/preview`,
        { responseType: 'blob' });
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = URL.createObjectURL(res.data);
      setPreview({ url: previewUrl.current, bytes: res.data.size });
    } catch (error) {
      // An error body arrives as a Blob because we asked for one — read it back out.
      let detail = 'Preview failed';
      try { detail = JSON.parse(await error.response.data.text()).detail || detail; } catch { /* keep default */ }
      toast.error(detail);
    }
  };

  const pushOne = async () => {
    if (!picked) return;
    setPushing(true);
    try {
      const res = await api.post(`/member-app-bridge/events/${eventId}/attendees/${picked}/push`);
      const r = res.data || {};
      // push_one never throws for a bad badge — the outcome is the body, so read it, don't assume 200 = good.
      if (GOOD.includes(r.status)) toast.success(`${r.ticketId}: ${r.status}`);
      else toast.warning(`${r.ticketId}: ${r.status} — ${r.reason || 'no reason given'}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  const watch = (taskId) => {
    if (poll.current) clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const res = await api.get(`/member-app-bridge/events/${eventId}/sync/${taskId}`);
        setTask(res.data);
        if (res.data.state !== 'running') {
          clearInterval(poll.current);
          poll.current = null;
          load();
        }
      } catch {
        clearInterval(poll.current);
        poll.current = null;
      }
    }, 2000);
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/member-app-bridge/events/${eventId}/sync`);
      setTask({ ...res.data, total: 0, done: 0, counts: {} });
      watch(res.data.task_id);
      toast.success('Sync started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed to start');
    } finally {
      setSyncing(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch { toast.error('Copy failed — select and copy manually'); }
  };

  const pushUrl = useMemo(() => {
    if (!form.base_url || !form.target_event_id) return '';
    return `${form.base_url.replace(/\/+$/, '')}/v1/integrations/badges/${form.target_event_id}`;
  }, [form.base_url, form.target_event_id]);

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const ready = link?.configured && link?.base_url && link?.target_event_id && link?.has_token;
  const last = task || history[0];
  const counts = last?.counts || {};
  const failures = last?.failures || [];
  const withBadges = attendees.length;

  return (
    <div className="space-y-4">
      {/* 1. Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Smartphone className="h-5 w-5" /> Member app badges</span>
            {link?.enabled
              ? <span className="text-sm font-normal text-green-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> On</span>
              : <span className="text-sm font-normal text-slate-500">Off</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Pushes the entry badges this event has already generated to the Kaizen Events member app, where
            each member opens their own badge on their phone. Badge generation here is not changed in any way.
            In the member app panel: open the event → <strong>Badges (Kaizen-QR)</strong> → <strong>Connect
            Kaizen-QR</strong>, then paste the three values it shows below.
          </p>
          <p className="text-xs text-slate-500">
            This is a different product from the <strong>Kaizen Events</strong> tab next to it, which only shares
            the name. That one pulls paid registrations in; this one pushes finished badges out.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="mab-url">Member app API URL</Label>
              <Input id="mab-url" placeholder="https://dashboard.kaizenevents.live/api/api"
                value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="mab-event">Member app event ID</Label>
              <Input id="mab-event" placeholder="11111111-2222-3333-4444-555555555555"
                value={form.target_event_id}
                onChange={(e) => setForm({ ...form, target_event_id: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="mab-key">
                Key{' '}
                {link?.has_token
                  ? <span className="text-green-700">(saved — type a new one only to replace it)</span>
                  : <span className="text-amber-800">(not set)</span>}
              </Label>
              <Input id="mab-key" type="password" autoComplete="off" placeholder="keb_…"
                value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">
                Generated in the member app panel and valid until it is regenerated there — it does not expire
                on its own. It can only attach a badge to someone already registered for that event.
              </p>
            </div>
          </div>

          {pushUrl && (
            <div className="rounded-md bg-slate-50 p-3 text-sm">
              <p className="text-slate-500">Badges will be posted to</p>
              <div className="flex items-center gap-2">
                <code className="text-xs break-all">{pushUrl}</code>
                <button type="button" onClick={() => copy(pushUrl)} aria-label="Copy push URL"
                  className="text-slate-500 hover:text-slate-900"><Copy className="h-4 w-4" /></button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save()} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save connection
            </Button>
            {link?.enabled
              ? <Button variant="outline" onClick={() => save({ enabled: false })} disabled={saving}>Pause bridge</Button>
              : <Button variant="outline" onClick={() => save({ enabled: true })} disabled={saving || !ready}>Turn bridge on</Button>}
          </div>
          {!ready && (
            <p className="text-xs text-amber-800">
              Fill in all three values and save before turning the bridge on.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 2. Prove it on one person before the run */}
      <Card>
        <CardHeader><CardTitle>Check one badge first</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            A badge carries a working entry QR, so prove the mapping on one real person before pushing the
            event. Preview renders the image without sending anything; push sends just that one.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mab-search">Attendee with a generated badge ({withBadges}{truncated ? '+' : ''})</Label>
              <Input id="mab-search" placeholder="Search by ticket ID, name, phone, email or club…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); loadHolders(search); } }}
                onBlur={() => loadHolders(search)} />
              <select className={selectClass} value={picked} onChange={(e) => { setPicked(e.target.value); setPreview(null); }}>
                <option value="">— choose —</option>
                {attendees.map((a) => (
                  <option key={a.attendee_id} value={a.attendee_id}>
                    {[a.ticket_id, a.name, a.club_name].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
              {truncated && (
                <p className="text-xs text-amber-800">
                  Showing the first {attendees.length}. Search to narrow it down.
                </p>
              )}
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={runPreview} disabled={!picked}>
                <Eye className="h-4 w-4 mr-2" /> Preview
              </Button>
              <Button onClick={pushOne} disabled={!picked || pushing || !link?.enabled}>
                {pushing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Push this one
              </Button>
            </div>
          </div>
          {preview && (
            <div className="space-y-2">
              <img src={preview.url} alt="Badge preview"
                className="max-h-96 rounded-md border bg-white object-contain" />
              <p className="text-xs text-slate-500">
                Exactly the bytes that would be sent — {(preview.bytes / 1024).toFixed(0)} KB,{' '}
                {advanced.image_format.toUpperCase()} at {advanced.image_width}px wide.
              </p>
            </div>
          )}
          {withBadges === 0 && (
            <p className="text-sm text-amber-800">
              No attendee in this event has a generated badge yet, so there is nothing to push.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 3. The whole event */}
      <Card>
        <CardHeader><CardTitle>Push the whole event</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={syncAll} disabled={syncing || !link?.enabled || task?.state === 'running'}>
              {syncing || task?.state === 'running'
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <RefreshCw className="h-4 w-4 mr-2" />}
              {task?.state === 'running' ? 'Syncing…' : 'Sync all badges'}
            </Button>
            <Button variant="outline" onClick={load}>Refresh</Button>
            {!link?.enabled && <span className="text-xs text-amber-800">Turn the bridge on first.</span>}
          </div>

          <p className="text-sm text-slate-600">
            Safe to run as often as you like: every badge is re-sent each time and the member app answers{' '}
            <strong>unchanged</strong> for any it already holds, so nothing is duplicated.
          </p>

          {last && (
            <div className="space-y-3 text-sm">
              <p>
                {task?.state === 'running'
                  ? <>Running — {last.done}/{last.total} done.</>
                  : <>Last run {fmt(last.finished_at || last.started_at)} — {last.done}/{last.total}, {last.state}.</>}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['matched', 'unchanged', 'needs_review', 'no_match', 'error'].filter((k) => counts[k]).map((k) => (
                  <div key={k} className="rounded-md bg-slate-50 p-3">
                    <p className="text-slate-500">{k.replace('_', ' ')}</p>
                    <p className="text-lg font-semibold">{counts[k]}</p>
                  </div>
                ))}
              </div>
              {last.error && (
                <p className="text-red-700 flex items-start gap-1">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {last.error}
                </p>
              )}
              {failures.length > 0 && (
                <details>
                  <summary className="cursor-pointer text-amber-800">
                    {failures.length} badge(s) the member app would not place by itself
                  </summary>
                  <ul className="list-disc ml-5 mt-1 text-amber-900">
                    {failures.slice(0, 50).map((f) => (
                      <li key={f.ticketId}><strong>{f.ticketId}</strong> — {f.status}: {f.reason}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-500 mt-1">
                    These are not errors here. The member app refuses to guess who a badge belongs to, so an
                    organiser resolves them on its Badges screen.
                  </p>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 4. Rendering options — sensible defaults, rarely touched */}
      <Card>
        <CardHeader><CardTitle>Image &amp; field options</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="mab-chapter">Chapter field</Label>
              <Input id="mab-chapter" placeholder="auto-detect" value={advanced.chapter_field}
                onChange={(e) => setAdvanced({ ...advanced, chapter_field: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">
                Custom field holding the chapter. Blank = detect it (anything matching chapter or club, else
                the club name).
              </p>
            </div>
            <div>
              <Label>Format</Label>
              <select className={selectClass} value={advanced.image_format}
                onChange={(e) => setAdvanced({ ...advanced, image_format: e.target.value })}>
                <option value="jpeg">JPEG — much smaller (recommended)</option>
                <option value="png">PNG — lossless, roughly 7× bigger</option>
              </select>
            </div>
            <div>
              <Label htmlFor="mab-width">Width (px)</Label>
              <Input id="mab-width" type="number" min={1200} max={2400} value={advanced.image_width}
                onChange={(e) => setAdvanced({ ...advanced, image_width: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">
                1200 is the floor on purpose — below it the QR stops decoding reliably.
              </p>
            </div>
            <div>
              <Label htmlFor="mab-q">JPEG quality</Label>
              <Input id="mab-q" type="number" min={60} max={95} value={advanced.image_quality}
                disabled={advanced.image_format !== 'jpeg'}
                onChange={(e) => setAdvanced({ ...advanced, image_quality: e.target.value })} />
            </div>
          </div>
          <Button variant="outline" onClick={() => save()} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save options
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberAppBridge;
